import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import request from 'lib/request';
import type { ReferenceLocationResponse } from 'types/api-responses';
import { addSurveyTracts, type SurveyRow } from './survey-rows';

/** One of the unit's tracts, as `GET /development_areas/:id` returns it. */
export interface DevelopmentAreaGeography {
  id: number;
  quarterCalls: string[] | null;
  referenceLocation: ReferenceLocationResponse;
}

export interface DevelopmentAreaResponse {
  id: number;
  name: string;
  developmentAreaGeographies?: DevelopmentAreaGeography[];
}

export interface SurveyRowsFromUnit {
  loading: boolean;
  /** Set when a unit was clicked but had nothing to hand over. */
  notice: string | null;
  /** Fetches the unit and folds its tracts into the rows. */
  load: (developmentAreaId: number) => void;
  dismiss: () => void;
}

/**
 * Fills survey rows from a clicked unit, whose geographies are already the survey
 * description it was built from. Tracts are added to the rows already there, so
 * clicking two units builds an area covering both.
 *
 * @param {Function} setRows - The rows setter, updated functionally so a click
 * folds into the current rows.
 * @return {SurveyRowsFromUnit} The loader and its state.
 */
export const useSurveyRowsFromUnit = (
  setRows: Dispatch<SetStateAction<SurveyRow[]>>
): SurveyRowsFromUnit => {
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  // The panel this runs in can be closed mid-request, and the reply would
  // otherwise land on an unmounted tree.
  const abort = useRef(new AbortController());
  useEffect(() => {
    const controller = abort.current;
    return () => controller.abort();
  }, []);

  const load = useCallback(
    async (developmentAreaId: number) => {
      setLoading(true);
      setNotice(null);

      const response = await request.get<DevelopmentAreaResponse>(
        `/development_areas/${developmentAreaId}`,
        abort.current
      );

      // An aborted read means the user left; there is nothing to report to them.
      if (response && 'error' in response && response.error === 'AbortError') {
        return;
      }

      setLoading(false);

      if (!response || 'error' in response) {
        setNotice('Could not read that unit.');
        return;
      }

      const geographies = response.developmentAreaGeographies ?? [];

      // Bespoke units carry no geographies, so there is nothing to copy — say so
      // rather than appearing to do nothing.
      if (geographies.length === 0) {
        setNotice(
          `${response.name} has no survey description to copy — draw its boundary instead.`
        );
        return;
      }

      setRows((current) =>
        addSurveyTracts(
          current,
          geographies.map((geography) => ({
            location: geography.referenceLocation,
            quarterCalls: geography.quarterCalls ?? [],
          }))
        )
      );
    },
    [setRows]
  );

  const dismiss = useCallback(() => setNotice(null), []);

  return { loading, notice, load, dismiss };
};
